"use client";

import { addEmail } from "@/app/actions";
import { useState } from "react";

const SubscribeBox = () => {
  const [subscribed, setSubscribed] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [isPending, setIsPending] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsPending(true);

    const formData = new FormData(event.target);
    const email = formData.get("email");
    setValidationError("");

    if (!email) {
      setValidationError("Please enter an email address");
      setIsPending(false);
      return;
    }

    if (!validateEmail(email)) {
      setValidationError("Please enter a valid email address");
      setIsPending(false);
      return;
    }

    try {
      await addEmail(email);
      setSubscribed(true);
    } catch (err) {
      setValidationError(err.toString());
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className="mx-auto mt-2 p-4 sm:p-6 w-full sm:w-[85%] rounded-lg bg-[#333] ring-1 ring-inset ring-gray-500/20 shadow-md">
      {/* <h3 className="text-lg font-medium text-white text-center mb-3">Stay Updated with Remote Opportunities</h3> */}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 sm:mx-auto w-full sm:w-fit">
          <div className="text-gray-300 text-sm sm:text-base whitespace-nowrap">
            Send new remote jobs to
          </div>

          <div className="w-full relative">
            <div className="flex flex-col sm:flex-row w-full items-center gap-2 sm:gap-3">
              <label className="input floating-label validator h-10 w-full">
                <span>Your email</span>
                <svg
                  className="h-4 w-4 opacity-70"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                >
                  <g
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    strokeWidth="2.5"
                    fill="none"
                    stroke="currentColor"
                  >
                    <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                  </g>
                </svg>
                <input
                  type="email"
                  name="email"
                  placeholder="you@email.com"
                  required
                  disabled={subscribed || isPending}
                />
              </label>
              <div className="validator-hint hidden">
                Enter valid email address
              </div>
              <div className="text-gray-300 whitespace-nowrap text-center sm:text-left">every week!</div>

              <button
                type="submit"
                className="btn border-0 w-full sm:w-auto px-4 sm:px-6 py-2 rounded-md bg-purple-600 text-white font-medium transition-all hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:bg-purple-800 disabled:opacity-70"
                disabled={subscribed || isPending}
              >
                {isPending ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading
                  </div>
                ) : subscribed ? (
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-1.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                    Subscribed
                  </div>
                ) : (
                  "Subscribe"
                )}
              </button>
            </div>
          </div>
        </div>
        {validationError && (
          <p className="mt-2 text-sm text-red-400 text-center">
            {validationError}
          </p>
        )}

        {subscribed && (
          <p className="mt-2 text-sm text-green-400 text-center">
            Successfully subscribed! We'll send you updates every week.
          </p>
        )}
      </form>

      <div className="mt-4 pt-3 border-t border-gray-500/20 text-center">
        <p className="flex flex-wrap text-sm text-gray-400 items-center justify-center gap-1 sm:gap-1.5">
          Join our
          <a
            href="https://discord.com/invite/Sfyy8wF57y"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-400 hover:text-purple-300 font-medium transition-colors inline-flex items-center gap-1"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 24 24"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Discord community
          </a>
          |
          <a
            href="https://www.reddit.com/r/realworkfromanywhere/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-orange-500 hover:text-orange-400 font-medium transition-colors inline-flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24"><path fill="currentColor" d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12a12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547l-.8 3.747c1.824.07 3.48.632 4.674 1.488c.308-.309.73-.491 1.207-.491c.968 0 1.754.786 1.754 1.754c0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87c-3.874 0-7.004-2.176-7.004-4.87c0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754c.463 0 .898.196 1.207.49c1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197a.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248c.687 0 1.248-.561 1.248-1.249c0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25c0 .687.561 1.248 1.249 1.248c.688 0 1.249-.561 1.249-1.249c0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094a.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913c.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463a.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73c-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>
            Subreddit
          </a>
          for real-time job alerts!
        </p>
      </div>
    </div>
  );
};

export default SubscribeBox;